/**
 * 重い解析の走査窓（§0.1 / U154 / I67）。
 * 全長一括は既定にしない。選択 ≥50 bp、さもなくば ≤100 kb なら全長、超はビューポート。
 */

import type { Interval, Topology } from '../sequence/interval';
import { intervalLength, resolveSpans } from '../sequence/interval';
import type { ViewRange } from '../view/viewport';

/** 選択を走査に使う最短（I67） */
export const SCAN_SELECTION_MIN_BP = 50;
/** これ以下なら選択が短くても全長を走査してよい */
export const SCAN_FULL_LENGTH_MAX_BP = 100_000;
/** 染色体級の暴走防止。これより広い指定は切る */
export const SCAN_HARD_MAX_BP = 2_000_000;

export interface ScanWindowInput {
  genomeLength: number;
  topology: Topology;
  view: ViewRange;
  selection: Interval | null;
  /** ユーザーが明示した範囲。あれば最優先 */
  explicit?: Interval | null;
}

export interface ScanWindow {
  interval: Interval;
  spans: Interval[];
  reason: 'explicit' | 'selection' | 'full' | 'viewport';
  truncated: boolean;
}

export function resolveScanWindow(input: ScanWindowInput): ScanWindow {
  const { genomeLength, topology } = input;
  let interval: Interval;
  let reason: ScanWindow['reason'];

  if (input.explicit && intervalLength(input.explicit) > 0) {
    interval = input.explicit;
    reason = 'explicit';
  } else if (input.selection && intervalLength(input.selection) >= SCAN_SELECTION_MIN_BP) {
    interval = input.selection;
    reason = 'selection';
  } else if (genomeLength <= SCAN_FULL_LENGTH_MAX_BP) {
    interval = { start: 0, end: genomeLength };
    reason = 'full';
  } else {
    interval = { start: input.view.start, end: input.view.end };
    reason = 'viewport';
  }

  let truncated = false;
  if (intervalLength(interval) > SCAN_HARD_MAX_BP) {
    interval = { start: interval.start, end: interval.start + SCAN_HARD_MAX_BP };
    truncated = true;
  }

  return {
    interval,
    spans: resolveSpans(interval, genomeLength, topology),
    reason,
    truncated,
  };
}
